import { useEffect, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { api } from '../../lib/api';
import type { ExtensionApiKey } from '../../types';

/**
 * AdminExtensionKeysPage — `/admin/extension-keys`
 *
 * Manages bearer-token API keys used by the Chrome capture extension to
 * post screenshots / page content to `/api/ingest/extension`. New keys are
 * shown ONCE on creation (after that only the hashed value is stored).
 */

export default function AdminExtensionKeysPage() {
  const [keys, setKeys] = useState<ExtensionApiKey[]>([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  const reload = async () => {
    try {
      setKeys(await api.get<ExtensionApiKey[]>('/api/mgmt/extension-keys'));
    } catch {
      // fail-soft
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const createKey = async (e: React.FormEvent) => {
    e.preventDefault();
    const r = await api.post<{ id: string; key: string }>('/api/mgmt/extension-keys', { name: newKeyName });
    setCreatedKey(r.key);
    setNewKeyName('');
    await reload();
  };

  const revoke = async (id: string) => {
    if (!confirm('Revoke this key? The extension installation using it will stop working.')) return;
    await api.del(`/api/mgmt/extension-keys/${id}`);
    await reload();
  };

  return (
    <AdminLayout>
      <h1>Extension keys</h1>

      {createdKey && (
        <div className="alert alert-warning">
          <strong>Copy this key now</strong> — it will not be shown again:
          <pre className="mb-0 mt-2 p-2 bg-light"><code>{createdKey}</code></pre>
        </div>
      )}

      <form className="row g-2 align-items-end mb-3" onSubmit={createKey}>
        <div className="col-md-6">
          <label htmlFor="key-name" className="form-label">Key name (e.g., "My laptop")</label>
          <input
            id="key-name"
            required
            className="form-control"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <button className="btn btn-primary w-100" type="submit">Create key</button>
        </div>
      </form>

      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Created</th>
            <th>Last used</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => (
            <tr key={k.id}>
              <td>{k.name}</td>
              <td className="small">{new Date(k.createdAt).toLocaleString()}</td>
              <td className="small">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '—'}</td>
              <td>
                {!k.revoked && (
                  <button className="btn btn-sm btn-outline-danger" onClick={() => revoke(k.id)}>
                    Revoke
                  </button>
                )}
                {k.revoked && <span className="badge bg-secondary">revoked</span>}
              </td>
            </tr>
          ))}
          {keys.length === 0 && (
            <tr>
              <td colSpan={4} className="text-muted small">No keys yet — create one above.</td>
            </tr>
          )}
        </tbody>
      </table>
    </AdminLayout>
  );
}
